<script setup>
import { computed } from 'vue';
import MultiSelect from 'dashboard/components-next/filter/inputs/MultiSelect.vue';

const props = defineProps({
  audienceList: {
    type: Array,
    required: true,
  },
  modelValue: {
    type: Array,
    default: () => [],
  },
  excludeModelValue: {
    type: Array,
    default: () => [],
  },
  hasError: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['update:modelValue', 'update:excludeModelValue']);

// Transform audienceList from {id, title} to {id, name} format
const transformedAudienceList = computed(() => {
  return (
    props.audienceList?.map(item => ({
      id: item.id,
      name: item.title,
    })) || []
  );
});

const excludeAudienceList = computed(() => {
  const selectedIds = props.modelValue?.map(a => a.id) || [];
  return transformedAudienceList.value.filter(a => !selectedIds.includes(a.id));
});

const audienceListOptions = computed(() => {
  const selectedIds = props.excludeModelValue?.map(a => a.id) || [];
  return transformedAudienceList.value.filter(
    a => !selectedIds?.includes(a.id)
  );
});

// Transform selected values to {id, name} format for MultiSelect
const localSelectedAudience = computed({
  get: () => {
    return (
      props.modelValue?.map(item => ({
        id: item.id,
        name: item.title || item.name,
      })) || []
    );
  },
  set: value => {
    // Transform back to {id, title} format for parent component
    const transformed =
      value?.map(item => ({
        id: item.id,
        title: item.name,
      })) || [];
    emit('update:modelValue', transformed);
  },
});

const localSelectedExcludeAudience = computed({
  get: () => {
    return (
      props.excludeModelValue?.map(item => ({
        id: item.id,
        name: item.title || item.name,
      })) || []
    );
  },
  set: value => {
    // Transform back to {id, title} format for parent component
    const transformed =
      value?.map(item => ({
        id: item.id,
        title: item.name,
      })) || [];
    emit('update:excludeModelValue', transformed);
  },
});
</script>

<template>
  <div class="audience-selector flex flex-col gap-4">
    <div class="flex flex-col gap-1">
      <label
        for="audience-select"
        class="text-sm font-medium text-n-slate-12"
        :class="{ 'text-n-ruby-11': hasError }"
      >
        {{ $t('CAMPAIGN.SMS.CREATE.FORM.AUDIENCE.LABEL') }}
      </label>
      <MultiSelect
        id="audience-select"
        v-model="localSelectedAudience"
        :options="audienceListOptions"
      />
      <span v-if="hasError" class="text-sm text-n-ruby-11">
        {{ $t('CAMPAIGN.ADD.FORM.AUDIENCE.ERROR') }}
      </span>
    </div>
    <div class="flex flex-col gap-1">
      <label
        for="exclude-audience-select"
        class="text-sm font-medium text-n-slate-12"
      >
        {{ $t('CAMPAIGN.ADD.FORM.EXCLUDE_AUDIENCE.LABEL') }}
      </label>
      <MultiSelect
        id="exclude-audience-select"
        v-model="localSelectedExcludeAudience"
        :options="excludeAudienceList"
      />
    </div>
  </div>
</template>
